import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { TokenService } from 'src/app/services/getData_token.service';
import { isAdminFlow } from 'src/app/services/url-utils';

@Component({
  selector: 'app-candidate-floating-chatbot',
  templateUrl: './candidate-floating-chatbot.component.html',
  styleUrls: ['./candidate-floating-chatbot.component.css'],
})
export class CandidateFloatingChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatContent') private chatContent!: ElementRef;

  isAdmin: boolean = false;
  isChatOpen: boolean = false;
  hasLoadedHistory: boolean = false;

  loading: boolean = false;
  userQuestion: string = '';
  chatHistory: any[] = [];
  displayLoader: boolean = false;
  private shouldScroll: boolean = false;

  // Ask a Person modal
  showAskPersonModal: boolean = false;
  showDeptDropdown: boolean = false;
  askPersonDepartment: string = '';
  askPersonQuestion: string = '';
  submittingAskPerson: boolean = false;
  askPersonMessage: string = '';
  askPersonMessageType: 'success' | 'error' | '' = '';

  private apiBase = `${environment.apiConfig.uri}/api/Chatbot`;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private tokenService: TokenService
  ) {}

  ngOnInit(): void {
    this.isAdmin = isAdminFlow();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContent) {
        this.chatContent.nativeElement.scrollTop = this.chatContent.nativeElement.scrollHeight;
      }
    } catch (err) {}
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;
    if (this.isChatOpen) {
      document.body.style.overflow = 'hidden';
      if (!this.hasLoadedHistory) {
        this.hasLoadedHistory = true;
        this.getChatHistory();
      }
    } else {
      document.body.style.overflow = '';
    }
  }

  async getChatHistory() {
    try {
      this.displayLoader = true;
      const response: any = await this.http.get(`${this.apiBase}/GetCandidateChotbotHistory`).toPromise();
      this.chatHistory = response?.data || [];
      this.shouldScroll = true;
    } catch (error) {
      console.error('Error fetching chat history:', error);
      this.chatHistory = [];
    }
    this.displayLoader = false;
  }

  async askQuestion() {
    if (!this.userQuestion.trim() || this.loading) return;

    this.loading = true;
    const question = this.userQuestion;
    this.userQuestion = '';

    this.chatHistory.push({ question: question, answer: '', isLoading: true });
    this.shouldScroll = true;

    const history = this.chatHistory.slice(0, -1).map((item: any) => ({
      Question: item.question,
      Answer: item.answer
    }));

    try {
      const response: any = await this.http.post(`${this.apiBase}/query`, {
        Query: question,
        History: history
      }).toPromise();

      this.chatHistory[this.chatHistory.length - 1] = {
        question: question,
        answer: response?.data?.answer || 'Sorry, I could not process your request.',
        isLoading: false
      };
    } catch (error) {
      console.error('Error sending question:', error);
      this.chatHistory[this.chatHistory.length - 1] = {
        question: question,
        answer: 'Sorry, an error occurred while processing your request. Please try again.',
        isLoading: false
      };
    } finally {
      this.loading = false;
      this.shouldScroll = true;
    }
  }

  startAgain() {
    this.http.delete(`${this.apiBase}/DeleteCandidateChatbotHistory`)
      .subscribe({
        next: () => {
          this.chatHistory = [];
          this.userQuestion = '';
        },
        error: (err) => {
          console.error('Error clearing chat history:', err);
        }
      });
  }

  closeAskPersonModal() {
    this.showAskPersonModal = false;
    this.showDeptDropdown = false;
    this.askPersonDepartment = '';
    this.askPersonQuestion = '';
    this.askPersonMessage = '';
    this.askPersonMessageType = '';
  }

  submitAskPerson() {
    if (!this.askPersonDepartment || !this.askPersonQuestion.trim()) return;

    this.submittingAskPerson = true;
    this.askPersonMessage = '';
    this.askPersonMessageType = '';

    const params = {
      department: this.askPersonDepartment,
      question: this.askPersonQuestion
    };

    this.http.post(`${this.apiBase}/SubmitAskPerson`, null, { params })
      .subscribe({
        next: () => {
          this.submittingAskPerson = false;
          this.askPersonMessage = 'Your question has been submitted successfully!';
          this.askPersonMessageType = 'success';
          this.askPersonDepartment = '';
          this.askPersonQuestion = '';
        },
        error: (err) => {
          console.error('Error submitting question:', err);
          this.submittingAskPerson = false;
          this.askPersonMessage = 'Failed to submit your question. Please try again.';
          this.askPersonMessageType = 'error';
        }
      });
  }

  formatAnswer(answer: string): any {
    if (!answer) return '';

    let text = answer
      .replace(/### (.*?)(\n|$)/g, '<br><strong style="font-size:15px">$1</strong><br>')
      .replace(/## (.*?)(\n|$)/g, '<br><strong style="font-size:16px">$1</strong><br>')
      .replace(/# (.*?)(\n|$)/g, '<br><strong style="font-size:17px">$1</strong><br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" style="color:inherit;text-decoration:underline;word-break:break-all">$1</a>')
      .replace(/(\d+\. )/g, '<br>$1')
      .replace(/(\- )/g, '<br><span style="padding-left:16px">&bull; </span>')
      .replace(/(?:\r\n|\r|\n)/g, '<br>')
      .replace(/(<br\s*\/?>){2,}/gi, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(text);
  }
}
